
import {Component, Prop, Vue} from 'vue-property-decorator';
import { RideitNotice } from '@/models/Rideit/RideitNotice';
import RideitNoticeForm from './RideitNoticeForm.vue';
import RideitNoticesTableItem, { INoticeTableItem } from './RideitNoticesTableItem.vue';
import { IDataTableHeader } from '@/typings/dataTable';
import { NoticeTargetType } from '@/models/Rideit/RideitNoticeTarget';
import { PassengerGroup } from '@/models/PassengerGroup';
import Destination from '@/components/Passenger/models/Destination';
import { IRideitCommunicationsLoadingStates } from '../RideitCommunications.vue';

@Component({
  components: {
    RideitNoticeForm,
    RideitNoticesTableItem,
  }
})
export default class RideitNoticesTable extends Vue {
  formRef = 'noticeForm' as const;

  @Prop({ default: () => [] }) private notices: RideitNotice[];
  @Prop({ default: '' }) private filter: string;
  @Prop({ default: '' }) private errorMessage: string;
  @Prop() private loading: IRideitCommunicationsLoadingStates;

  headers: IDataTableHeader[] = [
    { text: this.$t('generic.dateShort'), value: 'createdAt', sortable: true },
    { text: this.$t('generic.title'), value: 'title', sortable: false },
    { text: this.$t('rideIt.notice.text'), value: 'text', sortable: false },
    { text: this.$t('passenger.prop.destinations.main'), value: 'destinations', sortable: true },
    { text: this.$t('organization.passengerGroup.plural'), value: 'passengerGroups', sortable: true },
    { text: this.$t('generic.status'), value: 'displayedStatus', sortable: true, align: 'left' },
    { text: '', value: 'actions', sortable: false, align: 'right' }
  ];

  get shownItems()  {
    const normalizedSearch = this.filter.trim().toLowerCase();
    return this.notices.map(n => {
      return {
        createdAt: n.createdAt,
        notice: n,
        destinations: this.getdestinationsString(n),
        passengerGroups: this.getPassengerGroupsString(n),
        displayedStatus: n.displayedStatus,
      } as INoticeTableItem;
    }).filter(i => {
      const matches = [
        i.notice.title,
        i.notice.text,
        i.destinations,
        i.passengerGroups,
      ];
      return matches.some(m => m.toLowerCase().includes(normalizedSearch));
    });
  }

  getdestinationsString(notice: RideitNotice) {
    const destinationTargets = notice.targets.filter(t => t.targetType === NoticeTargetType.Destination);
    if (destinationTargets.length === 0) {
      return '-';
    }

    if (destinationTargets.length === 1 && !destinationTargets[0].destinationId) {
      return this.$t('generic.all');
    }
    const storeDestinations = this.$store.state.destination.destinations as Destination[];
    const destinationNames = destinationTargets.reduce((names: string[], target) => {
      const destination = storeDestinations.find((d: any) => d.id === target.destinationId);
      if (destination) {
        names.push(destination.alias);
      }
      return names;
    }, []);

    if (destinationNames.length === 0) {
      return '-';
    }

    return destinationNames.join(', ');
  }

  getPassengerGroupsString(notice: RideitNotice) {
    const passengerGroupTargets = notice.targets.filter(t => t.targetType === NoticeTargetType.PassengerGroup);
    if (passengerGroupTargets.length === 0) {
      return '-';
    }

    if (passengerGroupTargets.length === 1 && !passengerGroupTargets[0].passengerGroupId) {
      return this.$t('generic.all');
    }
    const storePassengerGroups = this.$store.state.organization.passengerGroups as PassengerGroup[];
    const passengerGroupNames = passengerGroupTargets.reduce((names: string[], target) => {
      const passengerGroup = storePassengerGroups.find((d: any) => d.id === target.passengerGroupId);
      if (passengerGroup) {
        names.push(passengerGroup.name);
      }
      return names;
    }, []);

    if (passengerGroupNames.length === 0) {
      return '-';
    }

    return passengerGroupNames.join(', ');
  }

  editNotice(notice: RideitNotice) {
    this.$emit('edit-notice', notice);
  }
}
